import './FormikTypeaheadInput.css';
import type { SearchEvent } from '@ipfabric/ui-components/AutocompleteInput/AutocompleteInput.types';
import { useClickAway } from '@ipfabric/ui-components/hooks/useClickAway';
import {
  DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT,
  useDebounce,
} from '@ipfabric/ui-components/hooks/useDebounce';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import { TAB } from '@ipfabric/ui-components/utils/keyboard/keyboardKeys';
import type { StringKey } from '@ipfabric/utils';
import classNames from 'classnames';
import { FormField } from 'components/FormField/FormField';
import { TextInput } from 'components/TextInput/TextInput';
import type { FormikProps } from 'formik';
import { type FormEvent, type JSX, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react';
import { fieldHasError } from 'utils/formik/fieldHasError';
import {
  type FormikAutoTrim,
  getTrimFunction,
} from 'utils/formik/formikAutoTrim';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

const DEFAULT_PAGE = 0;

interface Props<TFormikValues> {
  autoTrim?: FormikAutoTrim;
  formik: FormikProps<TFormikValues>;
  inputChangeDebounceInterval?: number;
  inputClassName?: string;
  loading?: boolean;
  menuClassName?: string;
  menuContent: ReactNode;
  name: StringKey<TFormikValues> | NestedFormikName;
  onFocus?: (e: SearchEvent) => void;
  onSearch: (e: SearchEvent) => void;
  placeholder?: string;
  wrapperClassName?: string;
  label?: ReactNode;
}

export const FormikTypeaheadInput = <TFormikValues,>(
  props: Props<TFormikValues>,
): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const debounceSearch = useDebounce(
    props.inputChangeDebounceInterval ?? DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT,
  );

  const field = props.formik.getFieldProps(props.name);
  const { error } = props.formik.getFieldMeta(props.name as string);
  const hasError = fieldHasError<TFormikValues>(props.formik, props.name);

  const closeMenu = useStableCallback(() => {
    setMenuOpen(false);
  });

  const wrapperRef = useClickAway<HTMLDivElement>(closeMenu);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const trimFunction = getTrimFunction(props.autoTrim);
    trimFunction(field.onChange)(e);
    const { value } = e.currentTarget;
    setPage(DEFAULT_PAGE);

    debounceSearch(() => {
      if (menuRef.current) {
        // Scroll to top to show new results
        menuRef.current.scrollTo(0, 0);
      }
      props.onSearch({
        nextPageNumber: DEFAULT_PAGE,
        inputValue: value.trim(),
      });
    });
  };

  const handleBlur = (e: FormEvent<HTMLInputElement>) => {
    field.onBlur(e);
  };

  const handleFocus = (e: FormEvent<HTMLInputElement>) => {
    if (!menuOpen) {
      setMenuOpen(true);
      props.onFocus?.({
        nextPageNumber: DEFAULT_PAGE,
        inputValue: e.currentTarget.value,
      });
    }
  };

  const handleMenuScroll = useStableCallback(() => {
    const menu = menuRef.current;
    // Scrolled to bottom?
    if (
      menu &&
      menu.scrollHeight - menu.scrollTop - menu.clientHeight < 1 &&
      inputRef.current
    ) {
      props.onSearch({
        nextPageNumber: page + 1,
        inputValue: inputRef.current.value,
      });
      setPage(page + 1);
    }
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.code === TAB) {
      closeMenu();
      field.onBlur(e);
    }
  };

  useEffect(() => {
    const scrollEventListener = new ManagedDomEventListeners();
    if (menuOpen && menuRef.current) {
      scrollEventListener.add([menuRef.current, 'scroll', handleMenuScroll]);
    }

    return () => {
      scrollEventListener.remove();
    };
  }, [menuOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: just trigger on submit
  useEffect(() => {
    closeMenu();
    inputRef.current?.blur();
  }, [props.formik.submitCount, closeMenu]);

  return (
    <div className='formik-type-ahead-field'>
      <FormField
        className={classNames(menuOpen && 'z-dropdown absolute w-full')}
        label={props.label}
        error={hasError ? error : undefined}
      >
        <div ref={wrapperRef}>
          <TextInput
            {...field}
            autoComplete='off'
            className={classNames(hasError && 'formik-error-scroll-anchor')}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            placeholder={props.placeholder}
            inputRef={inputRef}
            type='text'
            hasError={hasError}
            typeAhead
            loading={props.loading}
          />
          {menuOpen && (
            <div
              className='formik-type-ahead-field__menu'
              onClick={closeMenu}
              ref={menuRef}
            >
              {props.menuContent}
            </div>
          )}
        </div>
      </FormField>
    </div>
  );
};
